import React from "react";
import ReactDOM from "react-dom";
import moment from "moment";
import html2canvas from "html2canvas";

const DeliveryPodPrint = ({ deliveryDetail: delivery, shipments = [], entity }) => {
  if (!delivery) return null;

  const printTime = new Date().toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "medium",
  });

  // Sender: use customer if available, else vendor (same as shipment POD)
  const getSender = (shipment) =>
    shipment?.customer
      ? {
        name:
          shipment?.customerUser?.name ||
          shipment?.customer?.profileName ||
          "",
        phone: shipment?.customerUser?.phone || "",
        address: shipment?.customer?.address || "",
      }
      : {
        name: shipment?.vendor?.shopName || "",
        phone: shipment?.vendor?.phone || "",
        address: shipment?.vendor?.address || "",
      };

  const getReceiver = (shipment) => ({
    name: shipment?.receiver?.customerUser?.name || "",
    phone: shipment?.receiver?.phone || "",
    address: shipment?.receiver?.address || "",
  });

  const getWeight = (shipment) =>
    shipment?.weight
      ? `${shipment.weight} Kg`
      : shipment?.unit?.value && shipment?.unit?.unitType?.name
        ? shipment.unit.unitType.name.toLowerCase() === "weight"
          ? `${shipment.unit.value} Kg`
          : `${shipment.unit.value} ${shipment.unit.unitType.name}`
        : "N/A";

  const deliveredBy = delivery?.responsibleUser?.name ||
    (typeof delivery?.sendOutId === "object"
      ? delivery?.sendOutId?.sendOutId
      : delivery?.sendOutId) ||
    "N/A";

  // Fallback receiver from the shipment (same as shipment POD)
  const fallbackShipment = shipments[0];
  const receiveByName =
    delivery?.receiveBy ||
    fallbackShipment?.receiver?.customerUser?.name ||
    "N/A";
  const receiveByPhone =
    delivery?.receiverPhone || fallbackShipment?.receiver?.phone || "N/A";

  const entityLogoUrl =
    entity?.logo?.url || delivery?.entity?.logo?.url || "/logonew.png";
  const entityDisplayName =
    entity?.displayName ||
    entity?.name ||
    delivery?.entity?.displayName ||
    delivery?.entity?.name ||
    "";

  return (
    <div
      style={{
        fontFamily: "sans-serif",
        width: "210mm",
        boxSizing: "border-box",
        background: "white",
        color: "#000000",
        margin: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          border: "2px solid black",
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: 16,
            borderBottom: "2px solid black",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ flex: "0 0 auto", marginRight: "16px" }}>
            <img
              src={entityLogoUrl}
              alt="Logo"
              style={{ height: "60px" }}
            />
          </div>
          <div>
            <div
              style={{
                fontSize: 22,
                fontWeight: "bold",
                textAlign: "center",
              }}
            >
              Proof of Delivery
            </div>
            <div style={{ fontSize: 14, textAlign: "center", marginTop: 4 }}>
              {entityDisplayName}
            </div>
          </div>
        </div>

        {/* Delivery Details */}
        <div style={{ display: "flex", borderBottom: "1px solid #ccc" }}>
          <div style={{ flex: 1, padding: 16 }}>
            <div style={{ fontWeight: "bold", fontSize: 15, marginBottom: 6 }}>
              Delivery Details
            </div>
            <div style={{ fontSize: 14 }}>
              <div style={{ marginBottom: 4 }}>
                <strong>Delivery ID:</strong> #
                {delivery?.fulfillmentCompletionId || delivery?._id}
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>Status:</strong> {delivery?.status || "N/A"}
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>Delivered On:</strong>{" "}
                {delivery?.actualDeliveryTime
                  ? moment(delivery?.actualDeliveryTime).format(
                    "MM/DD/YYYY hh:mm A"
                  )
                  : "N/A"}
              </div>
            </div>
          </div>
          <div style={{ flex: 1, padding: 16, borderLeft: "1px solid #ccc" }}>
            <div style={{ fontWeight: "bold", fontSize: 15, marginBottom: 6 }}>
              Delivery Information
            </div>
            <div style={{ fontSize: 14 }}>
              <div style={{ marginBottom: 4 }}>
                <strong>Delivered By:</strong> {deliveredBy}
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>Created By:</strong> {delivery?.createdBy?.name || "N/A"}
              </div>
              <div style={{ marginBottom: 4 }}>
                <strong>Created Date:</strong>{" "}
                {delivery?.createdAt
                  ? moment(delivery?.createdAt).format("MM/DD/YYYY")
                  : "N/A"}
              </div>
            </div>
          </div>
        </div>

        {/* Shipment Sections (per shipment, like shipment POD) */}
        {shipments.length > 0 ? (
          shipments.map((shipment, idx) => {
            const sender = getSender(shipment);
            const receiver = getReceiver(shipment);
            const weight = getWeight(shipment);
            const shipmentLabel =
              shipments.length > 1
                ? `Shipment ${idx + 1} / ${shipments.length}`
                : "Shipment Details";
            return (
              <div key={shipment?._id || idx}>
                {/* Shipment Details | Package Information */}
                <div style={{ display: "flex", borderBottom: "1px solid #ccc" }}>
                  <div style={{ flex: 1, padding: 16 }}>
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: 15,
                        marginBottom: 6,
                      }}
                    >
                      {shipmentLabel}
                    </div>
                    <div style={{ fontSize: 14 }}>
                      <div style={{ marginBottom: 4 }}>
                        <strong>Order ID:</strong> #{shipment?.shipmentId || "N/A"}
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        <strong>From Branch:</strong>{" "}
                        {shipment?.sourceBranch?.name || "N/A"}
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        <strong>To Branch:</strong>{" "}
                        {shipment?.destinationBranch?.name || "N/A"}
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        <strong>Order Created Date:</strong>{" "}
                        {shipment?.createdAt
                          ? moment(shipment.createdAt).format("MM/DD/YYYY")
                          : "N/A"}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{ flex: 1, padding: 16, borderLeft: "1px solid #ccc" }}
                  >
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: 15,
                        marginBottom: 6,
                      }}
                    >
                      Package Information
                    </div>
                    <div style={{ fontSize: 14 }}>
                      <div style={{ marginBottom: 4 }}>
                        <strong>Weight:</strong> {weight}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Sender Information | Receiver Information */}
                <div style={{ display: "flex", borderBottom: "1px solid #ccc" }}>
                  <div style={{ flex: 1, padding: 16 }}>
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: 15,
                        marginBottom: 6,
                      }}
                    >
                      Sender Information
                    </div>
                    <div style={{ fontSize: 14 }}>
                      <div style={{ marginBottom: 4 }}>
                        <strong>Name:</strong> {sender.name || "N/A"}
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        <strong>Phone:</strong> {sender.phone || ""}
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        <strong>Address:</strong> {sender.address || ""}
                      </div>
                    </div>
                  </div>
                  <div
                    style={{ flex: 1, padding: 16, borderLeft: "1px solid #ccc" }}
                  >
                    <div
                      style={{
                        fontWeight: "bold",
                        fontSize: 15,
                        marginBottom: 6,
                      }}
                    >
                      Receiver Information
                    </div>
                    <div style={{ fontSize: 14 }}>
                      <div style={{ marginBottom: 4 }}>
                        <strong>Name:</strong> {receiver.name || "N/A"}
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        <strong>Phone:</strong> {receiver.phone || ""}
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        <strong>Address:</strong> {receiver.address || ""}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div
            style={{
              padding: 16,
              borderBottom: "1px solid #ccc",
              fontSize: 14,
              textAlign: "center",
            }}
          >
            No shipments found
          </div>
        )}

        {/* Receive By Information */}
        <div style={{ padding: 16, borderBottom: "1px solid #ccc" }}>
          <div style={{ fontWeight: "bold", fontSize: 15, marginBottom: 6 }}>
            Receive By Information
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div style={{ flex: 1 }}>
              <div>
                {delivery?.signature ? (
                  <img
                    src={delivery?.signature}
                    alt="Delivery Signature"
                    style={{
                      height: "auto",
                      width: "200px",
                      objectFit: "contain",
                      border: "1px solid #ccc",
                      background: "#fff",
                      display: "block",
                    }}
                  />
                ) : (
                  "No signature available"
                )}
              </div>
            </div>

            <div style={{ flex: 1 }}>
              <div>
                <div style={{ fontSize: 13, marginTop: 4, marginBottom: 6 }}>
                  <strong>Name:</strong> {receiveByName}
                </div>
                <div style={{ fontSize: 13, marginBottom: 6 }}>
                  <strong>Phone:</strong> {receiveByPhone}
                </div>
                <div style={{ fontSize: 13, marginBottom: 6 }}>
                  <strong>Received On:</strong>{" "}
                  {delivery?.actualDeliveryTime
                    ? moment(delivery?.actualDeliveryTime).format(
                      "MM/DD/YYYY hh:mm A"
                    )
                    : "N/A"}
                </div>
                {delivery?.deliveryNotes ? (
                  <div style={{ fontSize: 13, marginBottom: 4 }}>
                    <strong>Note:</strong> {delivery?.deliveryNotes}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            borderTop: "2px solid black",
            textAlign: "center",
            padding: 10,
            fontSize: 14,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: 12, color: "#555" }}>
            Printed on: {printTime}
          </div>
          <div>Thank you for choosing our logistics service.</div>
        </div>
      </div>
      <style>
        {`
          @media print {
            html, body, #delivery-pod, #delivery-pod > div {
              width: 210mm !important;
              height: 297mm !important;
              min-width: 210mm !important;
              min-height: 297mm !important;
              max-width: 210mm !important;
              max-height: 297mm !important;
              margin: 0 !important;
              padding: 0 !important;
              box-sizing: border-box !important;
              background: white !important;
            }
            @page {
              size: A4 portrait;
              margin: 0;
            }
          }
        `}
      </style>
    </div>
  );
};

/**
 * Opens POD in a new window for viewing / printing
 */
export const generateDeliveryPodPrint = (deliveryDetail, shipments, entity) => {
  const podWindow = window.open("", "_blank");
  if (!podWindow) {
    console.error("Failed to open new window for printing");
    return;
  }
  podWindow.document.write(`
    <html>
      <head>
        <title>Proof of Delivery</title>
        <style>
          @media print {
            body * { visibility: hidden; }
            #delivery-pod, #delivery-pod * { visibility: visible; }
            #delivery-pod { position: absolute; left: 0; top: 0; width: 100%; height: 100%; margin: 0; padding: 0; box-shadow: none; }
            @page { margin: 0; size: auto; }
          }
        </style>
      </head>
      <body>
        <div id="delivery-pod" style="display: flex; justify-content: center; align-items: center;"></div>
      </body>
    </html>
  `);
  podWindow.document.close();

  setTimeout(() => {
    const root = podWindow.document.getElementById("delivery-pod");
    if (root) {
      const element = document.createElement("div");
      root.appendChild(element);
      try {
        ReactDOM.render(
          <DeliveryPodPrint
            deliveryDetail={deliveryDetail}
            shipments={shipments}
            entity={entity}
          />,
          element
        );
      } catch (error) {
        console.error("ReactDOM.render error:", error);
      }
    } else {
      console.error("delivery-pod element not found");
    }
  }, 100);
};

/**
 * Converts an external image URL to a base64 data URL so html2canvas
 * can capture it without CORS issues
 */
const urlToDataUrl = async (url) => {
  if (!url || url.startsWith("data:")) return url;
  try {
    const response = await fetch(url, { mode: "cors" });
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Failed to convert image to data URL:", url, error);
    return url;
  }
};

/**
 * Renders POD off-screen and downloads it as a PNG image
 */
export const generateDeliveryPodImage = (deliveryDetail, shipments, entity) => {
  return new Promise((resolve, reject) => {
    const prepare = async () => {
      // Embed signature & logo (external file URLs) as base64 for html2canvas
      const preparedDelivery = { ...deliveryDetail };
      if (preparedDelivery?.signature) {
        preparedDelivery.signature = await urlToDataUrl(preparedDelivery.signature);
      }
      const preparedEntity = entity ? { ...entity } : null;
      if (preparedEntity?.logo?.url) {
        preparedEntity.logo = {
          ...preparedEntity.logo,
          url: await urlToDataUrl(preparedEntity.logo.url),
        };
      }
      return { preparedDelivery, preparedEntity };
    };

    const container = document.createElement("div");
    container.style.position = "fixed";
    container.style.top = "0";
    container.style.left = "0";
    container.style.zIndex = "-9999";
    container.style.pointerEvents = "none";
    container.style.color = "#000000";
    document.body.appendChild(container);

    prepare()
      .then(({ preparedDelivery, preparedEntity }) => {
        try {
          ReactDOM.render(
            <DeliveryPodPrint
              deliveryDetail={preparedDelivery}
              shipments={shipments}
              entity={preparedEntity}
            />,
            container
          );
        } catch (error) {
          document.body.removeChild(container);
          reject(error);
          return;
        }
        const waitForImages = (root) => {
          const imgs = Array.from(root.querySelectorAll("img"));
          if (imgs.length === 0) return Promise.resolve();
          return Promise.all(
            imgs.map((img) => {
              if (img.complete && img.naturalWidth > 0) return Promise.resolve();
              return new Promise((res) => {
                img.addEventListener("load", res, { once: true });
                img.addEventListener("error", res, { once: true });
                setTimeout(res, 3000);
              });
            })
          );
        };
        requestAnimationFrame(async () => {
          const target = container.firstElementChild || container;
          await waitForImages(target);
          await document.fonts.ready;
          try {
            const canvas = await html2canvas(target, {
              scale: 2,
              useCORS: true,
              backgroundColor: "#ffffff",
              width: target.scrollWidth,
              height: target.scrollHeight,
            });
            const link = document.createElement("a");
            const deliveryNo =
              deliveryDetail?.fulfillmentCompletionId ||
              deliveryDetail?._id ||
              "delivery";
            const todayDate = moment().format("YYYY-MM-DD");
            link.download = `POD_${deliveryNo}_${todayDate}.png`;
            link.href = canvas.toDataURL("image/png");
            link.click();
            resolve();
          } catch (error) {
            console.error("html2canvas error:", error);
            reject(error);
          } finally {
            ReactDOM.unmountComponentAtNode(container);
            document.body.removeChild(container);
          }
        });
      })
      .catch(reject);
  });
};

export default DeliveryPodPrint;
